/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { CartItem, CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import { MaptoCartResponseModel } from '@experience/shop/product-selector/models/mapto-cart-response.model';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { IsLineSetupCompletedModel } from '@experience/shop/product-selector/models/isLineSetupCompleted.model';
import { DEVICE_ASSESSSMENT_CONSTANT } from '@experience/device-assessment/device-assessment.constants';
import { ByodMappingModel } from '@experience/shop/product-selector/models/byod-mapping.model';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CART_REVIEW_CONST } from '@experience/shop/cart/constants/cart-review.constants';
import {
  CartLineItem,
  CartNonLineItem,
} from '@experience/shop/cart/interfaces/cart-review.interface';

export class ExtractFromCartResponseModel {
  constructor() {}

  /**
   * Function used to get list of plans from cart response
   * @param {CartLine} line
   * @param {number} index
   * @param {LineDeivceDetails} getCurrentIdDetails
   * @param {ObjectService} objectService
   * @param stateStoreUtilsService
   * @returns {LineDeivceDetails}
   */
  public static extractPlansObject(
    line: CartLine,
    index: number,
    getCurrentIdDetails: LineDeivceDetails,
    objectService: ObjectService,
    stateStoreUtilsService: StateStoreUtilsService
  ): LineDeivceDetails {
    let focusedTileIdDetails = getCurrentIdDetails;
    focusedTileIdDetails.currentLineNumber = index;
    focusedTileIdDetails.lineId = objectService.get(line, 'id');
    focusedTileIdDetails.selectedMsisdn = objectService.get(line, 'msisdn');
    if (objectService.get(line, 'imei')) {
      focusedTileIdDetails.byodCMMOrImei = ByodMappingModel.mapToImei(
        objectService.get(line, 'imei')
      );
    }
    const byodModalSimNumber = focusedTileIdDetails.byodSimData
      ? focusedTileIdDetails.byodSimData.simNumber
      : '';
    const simNumber = objectService.get(line, 'simNumber') || byodModalSimNumber;
    focusedTileIdDetails.plansFromCart = MaptoCartResponseModel.mapToPlans(
      line,
      index,
      objectService
    );
    focusedTileIdDetails = this.extractDeviceItems(
      focusedTileIdDetails,
      objectService.get(line, 'device'),
      objectService,
      stateStoreUtilsService,
      simNumber
    );
    focusedTileIdDetails.isUpgrade =
      objectService.get(line, `device.actionCode`) === CART_REVIEW_CONST.ADD &&
      objectService.get(line, 'plan.0.actionCode') === CART_REVIEW_CONST.RETAIN;
    return focusedTileIdDetails;
  }

  /**
   * Function used to get Non-Line items from cart
   * @param {LineDeivceDetails} tileDetails
   * @param {simNumber} simNumber
   * @param {Cart} devicesFromCart
   * @param {ObjectService} objectService
   * @param {StateStoreUtilsService} StateStoreUtilsService
   * @returns {LineDeivceDetails}
   */
  public static extractDeviceItems(
    tileDetails: LineDeivceDetails,
    devicesFromCart: CartItem,
    objectService: ObjectService,
    stateStoreUtilsService: StateStoreUtilsService,
    simNumber?: string
  ): LineDeivceDetails {
    const focusedTileIdDetails = tileDetails;
    this.checkAndUpdateCartReviewForm(
      focusedTileIdDetails,
      devicesFromCart,
      objectService,
      stateStoreUtilsService
    );
    const productOffers = stateStoreUtilsService.getSelectedItemInPDP(
      objectService.get(devicesFromCart, 'sku')
    );
    if (objectService.get(devicesFromCart, 'productSubType.0')) {
      switch (devicesFromCart.productSubType[0].toLocaleLowerCase()) {
        case DEVICE_ASSESSSMENT_CONSTANT.SIMCARD:
          focusedTileIdDetails.byodSimData = MaptoCartResponseModel.mapToSimKit(
            devicesFromCart,
            simNumber,
            objectService
          );
          focusedTileIdDetails.deviceDetails = focusedTileIdDetails.selectedDevice = undefined;
          break;
        case DEVICE_ASSESSSMENT_CONSTANT.HANDSET:
        case DEVICE_ASSESSSMENT_CONSTANT.TABLET:
        case DEVICE_ASSESSSMENT_CONSTANT.MOBILEINTERNET:
          focusedTileIdDetails.selectedDevice = focusedTileIdDetails.deviceDetails =
            MaptoCartResponseModel.mapToDevices(devicesFromCart, objectService, productOffers);
          break;
        default:
      }
    }
    focusedTileIdDetails.isLineSetupCompleted = IsLineSetupCompletedModel.isLineSetupCompleted(
      focusedTileIdDetails,
      objectService
    );
    return focusedTileIdDetails;
  }

  private static checkAndUpdateCartReviewForm(
    tileDetails: LineDeivceDetails,
    devicesFromCart: CartItem,
    objectService: ObjectService,
    stateStoreUtilsService: StateStoreUtilsService
  ): void {
    const oldCartItemId = objectService.get(devicesFromCart, 'oldCartItemId');
    if (oldCartItemId) {
      const existingDeviceInfo = stateStoreUtilsService.getNonLineItems(oldCartItemId);
      let updatedDeviceInfo: CartLineItem | CartNonLineItem = {};
      const isLineItem = objectService.get(tileDetails, 'lineTileType') === lineTypeEnum.LINE;
      if (isLineItem) {
        updatedDeviceInfo = {
          device: {
            defaultSku: objectService.get(existingDeviceInfo, 'defaultSku'),
            repEnteredSku: objectService.get(existingDeviceInfo, 'repEnteredSku'),
            imei: objectService.get(existingDeviceInfo, 'imei'),
            sim: objectService.get(existingDeviceInfo, 'sim'),
          },
          simKit: objectService.get(existingDeviceInfo, 'simKit'),
        };
      } else {
        updatedDeviceInfo = existingDeviceInfo;
      }
      const newCartItemId = isLineItem
        ? objectService.get(tileDetails, 'tileId')
        : objectService.get(devicesFromCart, 'id');
      stateStoreUtilsService.replaceCartItemId(
        oldCartItemId,
        newCartItemId,
        updatedDeviceInfo,
        isLineItem
      );
    }
  }
}
